import type { Department } from '@bliks-io/contracts';

/**
 * Tree node structure for departments
 */
export interface DepartmentTreeNode {
  department: Department;
  children: DepartmentTreeNode[];
  level: number;
  parentId: string | null;
  isLast: boolean;
}

/**
 * Flattened department for rendering with tree indicators
 */
export interface FlattenedDepartment {
  department: Department;
  level: number;
  prefix: string;
  isLast: boolean;
  parentIsLast: boolean[];
}

/**
 * Parse department path to extract hierarchy information
 * Path format: "rootId#parentId#id" or just "id" for root departments
 */
export function parseDepartmentPath(path: string | undefined): {
  segments: string[];
  level: number;
  parentId: string | null;
} {
  if (!path) {
    return { segments: [], level: 0, parentId: null };
  }

  const segments = path.split('#').filter(Boolean);
  const level = segments.length - 1;
  const parentId = segments.length > 1 ? segments[segments.length - 2] : null;

  return { segments, level, parentId };
}

/**
 * Build a tree structure from flat department list
 */
export function buildDepartmentTree(departments: Department[]): DepartmentTreeNode[] {
  const departmentMap = new Map<string, DepartmentTreeNode>();
  const rootNodes: DepartmentTreeNode[] = [];

  // First pass: Create all nodes
  departments.forEach((dept) => {
    const { level, parentId } = parseDepartmentPath(dept.path);
    departmentMap.set(dept._id, {
      department: dept,
      children: [],
      level,
      parentId,
      isLast: false
    });
  });

  // Second pass: Build tree structure
  departments.forEach((dept) => {
    const node = departmentMap.get(dept._id);
    if (!node) return;

    if (node.parentId) {
      const parent = departmentMap.get(node.parentId);
      if (parent) {
        parent.children.push(node);
      } else {
        // Parent doesn't exist, treat as root
        rootNodes.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });

  return rootNodes;
}

/**
 * Sort departments alphabetically by name
 */
function sortNodes(nodes: DepartmentTreeNode[]): DepartmentTreeNode[] {
  return nodes.sort((a, b) => {
    const nameA = a.department.name.toLowerCase();
    const nameB = b.department.name.toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

/**
 * Recursively sort the tree alphabetically at each level
 */
export function sortDepartmentTree(nodes: DepartmentTreeNode[]): DepartmentTreeNode[] {
  const sorted = sortNodes(nodes);

  sorted.forEach((node, index) => {
    // Mark if this is the last child at this level
    node.isLast = index === sorted.length - 1;

    // Recursively sort children
    if (node.children.length > 0) {
      node.children = sortDepartmentTree(node.children);
    }
  });

  return sorted;
}

/**
 * Generate tree prefix symbols based on level and position
 */
export function getTreePrefix(level: number, isLast: boolean): string {
  if (level === 0) {
    return '';
  }

  let prefix = '';

  // Add spacing for parent levels (no vertical lines)
  for (let i = 0; i < level - 1; i++) {
    prefix += '  '; // Reduced spacing, no connectors
  }

  // Add the connector for current level
  if (isLast) {
    prefix += '└─ ';
  } else {
    prefix += '├─ ';
  }

  return prefix;
}

/**
 * Flatten the tree in depth-first order for rendering
 */
export function flattenTreeDepthFirst(
  nodes: DepartmentTreeNode[],
  parentIsLast: boolean[] = []
): FlattenedDepartment[] {
  const flattened: FlattenedDepartment[] = [];

  nodes.forEach((node) => {
    const prefix = getTreePrefix(node.level, node.isLast);

    flattened.push({
      department: node.department,
      level: node.level,
      prefix,
      isLast: node.isLast,
      parentIsLast: [...parentIsLast]
    });

    // Recursively flatten children
    if (node.children.length > 0) {
      const childFlattened = flattenTreeDepthFirst(node.children, [...parentIsLast, node.isLast]);
      flattened.push(...childFlattened);
    }
  });

  return flattened;
}

/**
 * Main function to build and sort department tree, then flatten for rendering
 */
export function buildSortedFlatDepartmentList(departments: Department[]): FlattenedDepartment[] {
  if (!departments || departments.length === 0) {
    return [];
  }

  const tree = buildDepartmentTree(departments);
  const sortedTree = sortDepartmentTree(tree);
  return flattenTreeDepthFirst(sortedTree);
}

/**
 * Get the full hierarchy path of department names from root to current department
 * @param path - The department's materialized path (e.g., "rootId#parentId#id")
 * @param departments - The list of all departments
 * @returns Array of department names in hierarchical order from root to current department (inclusive)
 *
 * @example
 * // For a department with path "abc123#def456#ghi789"
 * // Returns: ["Corporate", "Engineering", "Backend", "UI Team"]
 */
export function getDepartmentPathNames(
  path: string | undefined,
  departments: Department[]
): string[] {
  if (!path || !departments) {
    return [];
  }

  const segments = path.split('#');

  // If there's only one segment, it's a root department - return just its name
  if (segments.length === 1) {
    const dept = departments.find((d) => d._id === segments[0]);
    return dept ? [dept.name] : [];
  }

  // Get all department IDs including the current department
  const pathIds = segments;

  // Map IDs to department names
  const pathNames = pathIds
    .map((id) => {
      const dept = departments.find((d) => d._id === id);
      return dept?.name;
    })
    .filter((name): name is string => name !== undefined);

  return pathNames;
}

/**
 * Calculate the hierarchical level of a department from its path
 * Root departments are level 1, their children are level 2, etc.
 *
 * @param path - The department's materialized path
 * @returns The hierarchy level (1 for root, 2 for first level children, etc.)
 *
 * @example
 * calculateDepartmentLevel('abc123') // 1 (root)
 * calculateDepartmentLevel('abc123#def456') // 2 (child of root)
 * calculateDepartmentLevel('abc123#def456#ghi789') // 3 (grandchild of root)
 */
export function calculateDepartmentLevel(path: string | undefined): number {
  if (!path) return 1;
  const segments = path.split('#').filter(Boolean);
  return segments.length;
}

/**
 * Extract parent department ID from path
 *
 * @param path - The department's materialized path
 * @returns The parent department ID or undefined if root department
 *
 * @example
 * extractParentIdFromPath('abc123') // undefined (root department)
 * extractParentIdFromPath('abc123#def456') // 'abc123'
 * extractParentIdFromPath('abc123#def456#ghi789') // 'def456'
 */
export function extractParentIdFromPath(path: string | undefined): string | undefined {
  const { parentId } = parseDepartmentPath(path);
  return parentId || undefined;
}

/**
 * Get parent department name from path
 *
 * @param path - The department's materialized path
 * @param departments - List of all departments
 * @returns The parent department name or '-' if no parent exists
 *
 * @example
 * getParentDepartmentName('abc123', departments) // '-' (root department)
 * getParentDepartmentName('abc123#def456', departments) // 'Engineering'
 */
export function getParentDepartmentName(
  path: string | undefined,
  departments: Department[]
): string {
  if (!path || !departments) return '-';
  const { parentId } = parseDepartmentPath(path);

  // Root department (no parent)
  if (!parentId) return '-';

  // Find parent department
  const parentDept = departments.find((d) => d._id === parentId);
  return parentDept?.name || '-';
}

/**
 * Find a department by ID
 *
 * @param id - The department ID to search for
 * @param departments - List of all departments
 * @returns The department or undefined if not found
 */
export function getDepartmentById(id: string, departments: Department[]): Department | undefined {
  return departments.find((d) => d._id === id);
}

/**
 * Check if a department is a root department (has no parent)
 *
 * @param path - The department's materialized path
 * @returns True if the department is at root level
 */
export function isRootDepartment(path: string | undefined): boolean {
  const { parentId } = parseDepartmentPath(path);
  return !parentId;
}
